// (6) Scaffold Path page: progress ladder + level content. Content is board+
// grade scoped server-side, so only matching scaffold levels are returned.
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import type { ScaffoldLevelSummary } from '@/types';
import { LevelProgressLadder } from '@/components/scaffold/LevelProgressLadder';
import { LevelContent } from '@/components/scaffold/LevelContent';
import { StudentPage, StudentPageHeader } from '@/components/ds';

const MAX_LEVEL = 3;

export function ScaffoldPage() {
  const { subtopicId = '' } = useParams();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const [sidebranched, setSidebranched] = useState<Set<number>>(new Set());

  const { data: levels, isLoading } = useQuery({
    queryKey: ['scaffold-levels', subtopicId],
    queryFn: () => apiGet<ScaffoldLevelSummary[]>(`/scaffold/${subtopicId}/levels`),
  });

  function advance() {
    setCompleted((c) => new Set(c).add(current));
    if (current < MAX_LEVEL) setCurrent((l) => l + 1);
  }

  return (
    <StudentPage>
      <StudentPageHeader
        breadcrumbs={[
          { label: 'Learn from the basics', to: '/learn/basics' },
          { label: `Rung ${current + 1}` },
        ]}
        title="Build from the basics"
        subtitle={`Rung ${current + 1} of ${MAX_LEVEL + 1}`}
      />
      {/* Banner */}
      <div
        className="relative overflow-hidden rounded-[22px] p-6"
        style={{ background: 'var(--brand-forest)' }}
      >
        <div
          className="text-fs-11.5 font-bold uppercase tracking-wide"
          style={{ color: 'var(--brand-lime)' }}
        >
          Scaffold · rung {current + 1} of {MAX_LEVEL + 1}
        </div>
        <div className="mt-1.5 text-2xl font-extrabold tracking-tight text-white">
          Let's build this one from the roots.
        </div>
        <p className="mt-1.5 max-w-xl text-fs-13.5 font-medium" style={{ color: '#b9d197' }}>
          Climb one rung at a time — nothing here is scored. Explore side-branches whenever you need
          more examples.
        </p>
      </div>

      {isLoading ? (
        <div className="h-64 w-full animate-pulse rounded-2xl bg-paper-sunk" />
      ) : !levels || levels.length === 0 ? (
        <p className="w-full py-10 text-center" style={{ color: 'var(--ink-3)' }}>
          No scaffold content for this sub-topic yet.
        </p>
      ) : (
        <div className="flex flex-col gap-4 lg:flex-row">
          <LevelProgressLadder
            current={current}
            completed={completed}
            sidebranched={sidebranched}
            onSelect={setCurrent}
          />
          <div className="flex-1">
            <LevelContent
              key={current}
              subtopicId={subtopicId}
              level={current}
              onAdvance={advance}
              onSidebranch={() => setSidebranched((s) => new Set(s).add(current))}
              onReattemptOriginal={() => {
                setCompleted((c) => new Set(c).add(MAX_LEVEL));
                navigate('/learn/basics');
              }}
            />
          </div>
        </div>
      )}
    </StudentPage>
  );
}
